import { useEffect } from "react";
import { resolveNextSelection } from "./formContainerListSelection";

interface UseActiveRecordSyncParams<T> {
  listItems: T[] | undefined;
  idKey: string;
  selectedId?: string | null;
  loading?: boolean;
  onSelect: (item: T) => void;
  onEmpty: () => void;
}

// Shared fix for the "stale active record" bug found repeatedly across the
// app (Vulnerability, Asset/Liability/Policy, System Manager, Action List):
// a list correctly refreshes after a delete, but a separately-held "active
// record" only gets re-derived by an id-gated effect (a route param, a row
// selection object) that a delete/refresh doesn't necessarily touch. This
// re-checks the active record against the CURRENT list on every list change,
// and asks the caller to select a fallback record or clear out, exactly the
// way formContainerListSelection.ts's resolveNextSelection already does for
// FormContainerWithList -- generalized so any module can reuse the same rule
// instead of hand-rolling its own (and getting it wrong, or forgetting it).
export function useActiveRecordSync<T extends Record<string, any>>({
  listItems,
  idKey,
  selectedId,
  loading,
  onSelect,
  onEmpty,
}: UseActiveRecordSyncParams<T>) {
  useEffect(() => {
    if (loading) return;

    const hasSelection = selectedId != null && selectedId !== "";
    const result = resolveNextSelection(listItems, idKey, selectedId, hasSelection);

    if (result.type === "empty") {
      onEmpty();
      return;
    }

    const next = (listItems as T[])[result.index];
    // Only act when the current selection is actually gone (or there wasn't
    // one) -- resolveNextSelection returns a valid index even when the
    // existing selection is still present, so this avoids re-selecting (and
    // potentially re-navigating/re-dispatching) on every render.
    if (!hasSelection || next?.[idKey] !== selectedId) {
      onSelect(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems, idKey, selectedId, loading]);
}
